import { useState } from "react"

import { useUpdateAdgroupBudgetBid } from "@/api/hooks/adsManager"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"

export type EditableAdgroupMetric = "budget" | "bid"

interface EditAdgroupMetricModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  adgroupId: string | null
  adgroupName?: string
  field: EditableAdgroupMetric
  initialValue?: number | null
}

function initialValueToInput(initialValue?: number | null): string {
  return initialValue != null && Number.isFinite(initialValue)
    ? String(initialValue)
    : ""
}

export function EditAdgroupMetricModal({
  open,
  onOpenChange,
  adgroupId,
  adgroupName,
  field,
  initialValue,
}: EditAdgroupMetricModalProps) {
  const update = useUpdateAdgroupBudgetBid()
  // Remount via parent `key` when target changes — no sync effect needed.
  const [value, setValue] = useState(() => initialValueToInput(initialValue))
  const [formError, setFormError] = useState<string | null>(null)

  const label = field === "budget" ? "Daily budget" : "Conversion bid"
  const title = field === "budget" ? "Edit budget" : "Edit bid"

  async function handleSubmit() {
    if (!adgroupId) return
    setFormError(null)
    const num = Number(value.trim())
    if (!Number.isFinite(num) || num <= 0) {
      setFormError(`${label} must be greater than 0`)
      return
    }
    try {
      const result = await update.mutateAsync({
        adgroupId,
        ...(field === "budget" ? { budget: num } : { bid: num }),
      })
      if (!result?.success) {
        setFormError(result?.message || "Update failed")
        return
      }
      onOpenChange(false)
    } catch (err) {
      setFormError((err as Error)?.message || "Update failed")
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-[#4a4a4a] bg-[#1f1f1f] text-white sm:max-w-sm">
        <DialogHeader>
          <DialogTitle className="text-white">{title}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-1">
          {adgroupName ? (
            <p className="truncate text-xs text-white/55" title={adgroupName}>
              {adgroupName}
            </p>
          ) : null}
          <div className="space-y-1.5">
            <Label htmlFor="ag-metric-value" className="text-white/80">
              {label}
            </Label>
            <Input
              id="ag-metric-value"
              type="number"
              min={0}
              step="0.01"
              inputMode="decimal"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault()
                  void handleSubmit()
                }
              }}
              className="border-[#5a5a5a] bg-[#2a2a2a] text-white"
              autoFocus
            />
          </div>
          {formError ? (
            <p className="text-xs text-red-400">{formError}</p>
          ) : null}
          {update.isError ? (
            <p className="text-xs text-red-400">
              {(update.error as Error)?.message || "Request failed"}
            </p>
          ) : null}
        </div>
        <DialogFooter className="gap-2 sm:gap-0">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="rounded border border-[#5a5a5a] bg-[#2a2a2a] px-3 py-1.5 text-xs text-white/80 hover:bg-[#3a3a3a]"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={update.isPending}
            onClick={() => void handleSubmit()}
            className="rounded border border-[#7488e8] bg-[#3a4580] px-3 py-1.5 text-xs text-white hover:bg-[#4a55a0] disabled:opacity-60"
          >
            {update.isPending ? "Saving…" : "Save"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
